import { useCallback, useState } from "react"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseFileDownloadReturn {
  downloadFile: (url: string, fileName: string, headers?: HeadersInit) => Promise<boolean>
  isLoading: boolean
  error: string | undefined
}

export const useFileDownload = (): UseFileDownloadReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const downloadFile = useCallback(
    async (url: string, fileName: string, headers?: HeadersInit): Promise<boolean> => {
      setIsLoading(true)
      setError(undefined)

      try {
        const response = await fetch(url, headers ? { headers } : undefined)

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()

        const contentType = response.headers.get("content-type")
        if (!contentType) {
          console.warn("No content type provided for download")
        }

        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement("a")
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()

        URL.revokeObjectURL(blobUrl)

        setIsLoading(false)
        return true
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Download failed"
        console.error("File download error:", errorMessage)

        setError(errorMessage)
        setIsLoading(false)
        notify({
          type: NotificationType.Error,
          autoHideDuration: 60_000,
          content: (
            <div>
              Couldn't download file. Try{" "}
              <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                opening it directly
              </a>
            </div>
          ),
        })
        return false
      }
    },
    [notify]
  )

  return { downloadFile, isLoading, error }
}
